import {
  Flame,
  Car,
  Wind,
  Cog,
  Zap,
  Thermometer,
  Droplets,
  Snowflake,
  ClipboardList,
  FileSpreadsheet,
  Plug,
  Archive,
  Plane,
  Truck,
  Bike,
  Fuel,
  Leaf,
  FlaskConical,
  Layers,
  Mountain,
  Hammer,
  Building2,
  Atom,
  CloudFog,
  Users,
  DollarSign,
  Banknote,
  Camera,
  Pencil,
  PenLine,
  UploadCloud,
  RefreshCw,
  ScanLine,
  type LucideIcon,
} from 'lucide-react';
import { type TypeCardOption } from './TypeCardSelector';
import {
  AssetType,
  EmissionScope,
  Scope1ActivityType,
  Scope2ActivityType,
  ActivityType,
  FuelType,
  VehicleType,
  FugitiveGasType,
  RefrigerantGasType,
  ProcessType,
  ActivityUnit,
  type AlternativeFactor,
} from '../../types/emissions';
import {
  FUEL_TYPE_LABEL_ENTRIES,
  VEHICLE_TYPE_LABEL_ENTRIES,
  GAS_TYPE_LABEL_ENTRIES,
  PROCESS_TYPE_LABEL_ENTRIES,
  COUNTRY_CODES,
  SUPRANATIONAL_REGIONS,
  type Region,
} from '@g17eco/emissions-config';
import { ButtonVariant } from '@g17eco/ui-modern/components/ui/button';
export {
  UNIT_LABELS,
  FUEL_TYPE_LABELS,
  VEHICLE_TYPE_LABELS,
  REFRIGERANT_TYPE_LABELS,
  FUGITIVE_GAS_TYPE_LABELS,
  GAS_TYPE_LABELS,
  PROCESS_TYPE_LABELS,
} from '@g17eco/emissions-config';

export type FormStep =
  | 'method'
  | 'entry'
  | 'stub-entry'
  | 'activity'
  | 'details'
  | 'review'
  | 'evidence'
  | 'result'
  | 'upload'
  | 'preview-import'
  | 'import-complete'
  | 'asset-select'
  | 'asset-method'
  | 'asset-bulk-upload'
  | 'asset-details'
  | 'asset-activities'
  | 'connect-utility-request'
  | 'connect-financial-request'
  | 'connect-travel-request'
  | 'by-spend-request'
  | 'bulk-spend-request'
  | 'employee-survey-request';

/**
 * How the user wants to enter emissions data. Type is a string union (so raw
 * string literals like `'from-inventory'` remain assignable throughout
 * develop's existing code paths) and `DataMethod` (below) is a const map of
 * named aliases for semantic use (`DataMethod.ByAsset`). Changing the
 * underlying strings would break any localStorage / URL state — don't
 * rename them without a migration plan.
 */
export type DataMethod =
  | 'log-activity'
  | 'bulk-upload'
  | 'from-inventory'
  | 'connect-utility'
  | 'connect-financial'
  | 'connect-travel'
  | 'scan-invoice'
  | 'by-spend'
  | 'bulk-spend'
  | 'employee-survey';

export const DataMethod = {
  ByActivity: 'log-activity',
  BulkUpload: 'bulk-upload',
  ByAsset: 'from-inventory',
  ConnectUtility: 'connect-utility',
  ConnectFinancial: 'connect-financial',
  ConnectTravel: 'connect-travel',
  ScanInvoice: 'scan-invoice',
  BySpend: 'by-spend',
  BulkSpend: 'bulk-spend',
  EmployeeSurvey: 'employee-survey',
} as const satisfies Record<string, DataMethod>;

/**
 * Data methods that still route to the placeholder request-access mockup
 * (a provider grid + "Request Access" CTA). Scan Invoice was originally in
 * this list but is now a real wizard (GU-6981) — see `SCAN_INVOICE_STEPS`
 * below — so it's excluded here too.
 */
export type RequestMethod = Exclude<
  DataMethod,
  'log-activity' | 'bulk-upload' | 'from-inventory' | 'scan-invoice'
>;

export const REQUEST_ACCESS_STEPS: readonly FormStep[] = [
  'connect-utility-request',
  'connect-financial-request',
  'connect-travel-request',
  'by-spend-request',
  'bulk-spend-request',
  'employee-survey-request',
];

export const REQUEST_METHOD_TO_STEP: Record<RequestMethod, FormStep> = {
  [DataMethod.ConnectUtility]: 'connect-utility-request',
  [DataMethod.ConnectFinancial]: 'connect-financial-request',
  [DataMethod.ConnectTravel]: 'connect-travel-request',
  [DataMethod.BySpend]: 'by-spend-request',
  [DataMethod.BulkSpend]: 'bulk-spend-request',
  [DataMethod.EmployeeSurvey]: 'employee-survey-request',
};
/**
 * A single staged row in the new "Add Activities" entry step (GU-7008).
 *
 * Each row is built from one trip through the existing `AssetActivitiesStep`
 * modal (manual add) or one row of a parsed XLSX (bulk import). Rows live
 * in client memory until the user clicks Submit on the Review step, at
 * which point they're persisted as one or more `BulkImport` records (one
 * per `bulkGroupId`, plus one for all manual rows combined).
 *
 * `status` is the per-row submission lifecycle:
 *   draft   — staged, not yet submitted
 *   pending — included in an in-flight `POST /imports` request
 *   created — server confirmed creation
 *   failed  — server rejected this row; `errorMessage` carries why
 */
export interface StagedRow {
  /** Stable client-side id; survives reorder/delete. Generated with `crypto.randomUUID()`. */
  id: string;
  /** The full server-bound payload, ready to ship to `POST /imports`. */
  request: AssetActivityEntry;
  /**
   * Group id linking this row to a bulk-import batch. Undefined means the
   * row was added manually via the `+ Add Activity` modal. The Review-step
   * submit logic groups by this field to know which rows share a source
   * file (and therefore which `BulkImport` they're persisted under).
   */
  bulkGroupId?: string;
  /**
   * Auto-resolved factor returned by `POST /factor-search` after the row's
   * classification fields are complete. The row's "system-selected"
   * factor — what the server would pick today if we submitted now.
   */
  resolvedFactorId?: string;
  resolvedFactorName?: string;
  resolvedFactorValue?: number;
  resolvedFactorUnit?: string;
  /**
   * True once the factor-search effect has completed for this row, even
   * if it returned no alternatives. Lets the staging table distinguish
   * "still resolving" (false) from "resolved with nothing found" (true,
   * but no resolvedFactorId).
   */
  resolveAttempted?: boolean;
  /**
   * User-overridden factor when they picked a non-default alternative from
   * the inline picker. Falls back to `resolvedFactorId` when undefined; on
   * submit we send the override (or auto-resolved) id as the row's
   * `climatiqFactorId`.
   */
  overrideFactorId?: string;
  overrideFactorName?: string;
  overrideFactorValue?: number;
  overrideFactorUnit?: string;
  /** Cached list of alternatives from the search — feeds the inline picker. */
  factorAlternatives?: AlternativeFactor[];
  estimatedCo2e?: number;
  /** Server-confirmed co₂e once the row has been created. */
  finalCo2e?: number;
  status: 'draft' | 'pending' | 'created' | 'failed';
  errorMessage?: string;
  /** Server-side `_id` of the created activity record after submit. */
  createdActivityId?: string;
}

/**
 * A bulk-import batch tracked in entry-step state. Holds the user's
 * uploaded XLSX/CSV `File` object until submit, plus the scope/activityType
 * the user chose in the bulk-import modal's first screen (needed by the
 * `POST /imports` body).
 *
 * Manual-only submissions don't need a `BulkGroup` — those rows have
 * `bulkGroupId === undefined`.
 */
export interface BulkGroup {
  /** Stable id, also stamped on every row in this group. */
  id: string;
  /** Original uploaded file. Held in memory until the Review submit. */
  file: File;
  /** Display label for the toolbar source-file badge. */
  filename: string;
  /**
   * The activity-flow's bulk uploads carry a single scope; the
   * asset-flow's bulk uploads have no meaningful scope (each asset can
   * have activities of mixed scopes), so this is optional. The source
   * of truth for asset-bulk is whatever's on the parsed `StagedAsset`
   * entries linked by `bulkGroupId`.
   */
  scope?: EmissionScope;
  activityType?: ActivityType | string;
}

export interface AssetActivityEntry {
  /** Tracks which suggested row created this entry (for badge counts) */
  sourceLabel?: string;
  scope: EmissionScope;
  activityType: ActivityType;
  quantity: string;
  unit: ActivityUnit | '';
  fuelType: FuelType | '';
  vehicleType: VehicleType | '';
  refrigerantType: RefrigerantGasType | FugitiveGasType | '';
  processType: ProcessType | '';
  region: string;
  regionFallback: boolean;
  yearFallback: boolean;
  periodStart: string;
  periodEnd: string;
  name: string;
  dataSource: string;
  notes: string;
}

/**
 * Activity-level form fields that exist regardless of which data method or
 * asset type the user is working with. These mirror the columns on a single
 * `AssetActivityEntry` plus the wizard's "name" for one-off activity submits.
 */
export interface ActivityFormData {
  scope: EmissionScope | '';
  activityType: ActivityType | '';
  periodStart: string;
  periodEnd: string;
  quantity: string;
  unit: ActivityUnit | '';
  fuelType: FuelType | '';
  vehicleType: VehicleType | '';
  refrigerantType: RefrigerantGasType | FugitiveGasType | '';
  processType: ProcessType | '';
  region: string;
  regionFallback: boolean;
  yearFallback: boolean;
  dataSource: string;
  notes: string;
  name: string;
}

/**
 * One staged asset pending submit. Mirrors the wire shape consumed by
 * `POST /assets/bulk-import` (`{ asset, activities }` per row) so manual
 * single-asset and XLSX-bulk paths produce the same shape and share one
 * submit code path. `bulkGroupId` links rows that came from the same
 * uploaded file — undefined for the manual path.
 */
export interface StagedAsset {
  /** Stable client-side id; survives reorder/delete. */
  id: string;
  /** Set when this asset came from an XLSX upload; manual rows leave it undefined. */
  bulkGroupId?: string;
  /** Asset metadata block, ready to ship to `POST /assets/bulk-import`. */
  asset: import('../../types/emissions').CreateAssetRequest;
  /** Activities staged against this asset; same shape as the Usage flow. */
  activities: StagedRow[];
}

/**
 * Asset-wizard fields. The wizard stages assets in the same shape the
 * server consumes — `stagedAssets[i] = { asset: CreateAssetRequest,
 * activities: StagedRow[] }` — so the manual-add path and the bulk-XLSX
 * path produce identical wire shapes and share one submit code path.
 *
 * Manual path: one entry, populated by `AssetDetailsStep` and
 * `AssetActivitiesStep`.
 *
 * Bulk path: N entries, each carrying the same `bulkGroupId` so the
 * submit can fire one `POST /assets/bulk-import` per file and attach
 * the source XLSX as evidence.
 */
export interface BaseAssetFormData {
  /**
   * Asset type chosen on `asset-select`. Held separately from
   * `stagedAssets[i].asset.assetType` because the bulk path picks the
   * type once at the top of the flow (XLSX template type) and stamps
   * it onto every parsed asset; the manual path picks it once and
   * starts a single `stagedAssets[0]`.
   */
  assetType: AssetType | '';
  stagedAssets: StagedAsset[];
}

/**
 * Wizard-wide form state. Activity-level fields cover the Usage flow's
 * single-activity submit; asset-level fields cover the asset wizard.
 * The two halves don't overlap — fields stay in one bag for
 * convenience but are read by disjoint step components.
 */
export type FormData = ActivityFormData & BaseAssetFormData;

export const INITIAL_FORM_DATA: FormData = {
  scope: '',
  activityType: '',
  periodStart: '',
  periodEnd: '',
  quantity: '',
  unit: '',
  fuelType: '',
  vehicleType: '',
  refrigerantType: '',
  processType: '',
  region: '',
  regionFallback: true,
  yearFallback: true,
  dataSource: '',
  notes: '',
  name: '',
  assetType: '',
  stagedAssets: [],
};

/**
 * Wrap an `AssetActivityEntry` (the shape produced by the existing
 * Add-Activity modal) into a `StagedRow` for the entry-step table.
 *
 * `bulkGroupId` is left undefined for manual rows; bulk-imported rows get
 * it stamped at the call-site after parsing the XLSX.
 */
export const createStagedRow = (
  request: AssetActivityEntry,
  bulkGroupId?: string,
): StagedRow => ({
  id: crypto.randomUUID(),
  request,
  bulkGroupId,
  status: 'draft',
});

/**
 * Build an empty `StagedAsset` for the manual path. `AssetSelectStep`
 * calls this when the user picks an asset type so `stagedAssets[0]`
 * exists before `AssetDetailsStep` starts writing into it.
 */
export const createEmptyStagedAsset = (assetType: AssetType): StagedAsset => ({
  id: crypto.randomUUID(),
  asset: { name: '', assetType },
  activities: [],
});

/**
 * Patch the metadata of the staged asset at `index`. Caller passes the
 * subset of `CreateAssetRequest` fields they edited; everything else
 * is preserved.
 */
export const updateStagedAssetMeta = (
  stagedAssets: StagedAsset[],
  index: number,
  patch: Partial<import('../../types/emissions').CreateAssetRequest>,
): StagedAsset[] =>
  stagedAssets.map((s, i) =>
    i === index ? { ...s, asset: { ...s.asset, ...patch } } : s,
  );

/**
 * Replace the activities array on the staged asset at `index`. Used by
 * `AssetActivitiesStep` per-block edits (add row, edit row, delete row,
 * factor override, etc.).
 */
export const updateStagedAssetActivities = (
  stagedAssets: StagedAsset[],
  index: number,
  next: StagedRow[] | ((prev: StagedRow[]) => StagedRow[]),
): StagedAsset[] =>
  stagedAssets.map((s, i) => {
    if (i !== index) {
      return s;
    }
    const activities = typeof next === 'function' ? next(s.activities) : next;
    return { ...s, activities };
  });

export const SCOPE1_ACTIVITY_TYPES = [
  {
    value: Scope1ActivityType.StationaryCombustion,
    label: 'Stationary Combustion',
    description: 'Fuel burned in equipment like boilers, furnaces',
    Icon: Flame,
    iconBg: 'tw:bg-orange-100',
    iconColor: 'tw:text-orange-600',
  },
  {
    value: Scope1ActivityType.MobileCombustion,
    label: 'Mobile Combustion',
    description: 'Vehicles and mobile equipment',
    Icon: Car,
    iconBg: 'tw:bg-blue-100',
    iconColor: 'tw:text-blue-600',
  },
  {
    value: Scope1ActivityType.FugitiveEmissions,
    label: 'Fugitive Emissions',
    description: 'HVAC and refrigeration systems',
    Icon: Wind,
    iconBg: 'tw:bg-teal-100',
    iconColor: 'tw:text-teal-600',
  },
  {
    value: Scope1ActivityType.ProcessEmissions,
    label: 'Process Emissions',
    description: 'Industrial processes',
    Icon: Cog,
    iconBg: 'tw:bg-slate-100',
    iconColor: 'tw:text-slate-600',
  },
];

export const SCOPE2_ACTIVITY_TYPES = [
  {
    value: Scope2ActivityType.PurchasedElectricity,
    label: 'Purchased Electricity',
    description: 'Electricity from the grid',
    Icon: Zap,
    iconBg: 'tw:bg-yellow-100',
    iconColor: 'tw:text-yellow-600',
  },
  {
    value: Scope2ActivityType.PurchasedHeating,
    label: 'Purchased Heating',
    description: 'District heating',
    Icon: Thermometer,
    iconBg: 'tw:bg-red-100',
    iconColor: 'tw:text-red-500',
  },
  {
    value: Scope2ActivityType.PurchasedSteam,
    label: 'Purchased Steam',
    description: 'Steam purchased from third party',
    Icon: Droplets,
    iconBg: 'tw:bg-cyan-100',
    iconColor: 'tw:text-cyan-600',
  },
  {
    value: Scope2ActivityType.PurchasedCooling,
    label: 'Purchased Cooling',
    description: 'District cooling',
    Icon: Snowflake,
    iconBg: 'tw:bg-sky-100',
    iconColor: 'tw:text-sky-500',
  },
];

export const SCOPE3_COMING_SOON = [
  // {
  //   label: 'Business Travel',
  //   description: 'Flights, rail, hotels',
  //   Icon: Plane,
  //   iconBg: 'tw:bg-violet-100',
  //   iconColor: 'tw:text-violet-600',
  // },
  // {
  //   label: 'Employee Commute',
  //   description: 'Staff travel to work',
  //   Icon: Train,
  //   iconBg: 'tw:bg-indigo-100',
  //   iconColor: 'tw:text-indigo-600',
  // },
  // {
  //   label: 'Purchased Goods',
  //   description: 'Upstream supply chain',
  //   Icon: ShoppingCart,
  //   iconBg: 'tw:bg-pink-100',
  //   iconColor: 'tw:text-pink-600',
  // },
  {
    label: 'Freight & Logistics',
    description: 'Transportation of goods',
    Icon: Truck,
    iconBg: 'tw:bg-amber-100',
    iconColor: 'tw:text-amber-600',
  },
];


/**
 * Region picker options for the activity wizard.
 *
 * Derived from `COUNTRY_CODES` (every Climatiq-supported country) plus
 * the supranational sentinels (`GLOBAL`, `EU`). Single source of truth
 * is `@g17eco/emissions-config/countries.ts`.
 *
 * Supranationals come first so users see the most-common picks at the
 * top; the rest follow in `COUNTRY_CODES`'s alphabetical-by-name order.
 */
const SUPRANATIONAL_LABELS: Record<typeof SUPRANATIONAL_REGIONS[number], string> = {
  GLOBAL: 'Global Average',
  EU: 'European Union',
};

export const REGION_OPTIONS: { value: Region; label: string }[] = [
  ...SUPRANATIONAL_REGIONS.map((code) => ({ value: code, label: SUPRANATIONAL_LABELS[code] })),
  ...COUNTRY_CODES.map(([code, name]) => ({ value: code, label: name })),
];

/**
 * Shape for the top-of-page progress indicator. `id` can be a FormStep for real
 * wizard steps, or `ghost:xxx` for preview-only flows where steps 3-5 are
 * visual placeholders only and never navigated to.
 */
export interface StepDescriptor {
  id: FormStep | `ghost:${string}`;
  label: string;
}

/**
 * Steps for the log-activity multi-add flow (GU-7008 redesign).
 *
 * Mirrors the asset-detail page: an `entry` step with an activities table +
 * toolbar (Add / Bulk Add / OCR), a `review` step that calculates emissions
 * per staged row and lets the user inspect resolved factors, then evidence
 * and result. The original `activity → details → review` legacy steps are
 * retained on the wider `FormStep` union for the asset wizard and edit
 * flows but are no longer reachable from `log-activity`.
 */
export const LOG_ACTIVITY_STEPS: StepDescriptor[] = [
  { id: 'method', label: 'Data Method' },
  { id: 'entry', label: 'Add Activities' },
  { id: 'review', label: 'Review' },
  { id: 'evidence', label: 'Attach Evidence' },
  { id: 'result', label: 'Result' },
];

export const BULK_UPLOAD_STEPS: StepDescriptor[] = [
  { id: 'method', label: 'Data Method' },
  { id: 'activity', label: 'Activity Type' },
  { id: 'upload', label: 'Upload File' },
  { id: 'preview-import', label: 'Preview' },
  { id: 'import-complete', label: 'Complete' },
];

export const ASSET_WIZARD_STEPS: StepDescriptor[] = [
  { id: 'method', label: 'Data Method' },
  { id: 'asset-select', label: 'Select Asset' },
  { id: 'asset-details', label: 'Asset Details' },
  // Asset-activities is the submit step — its in-card CTA fires
  // `createBulkAssetImport` then advances to evidence.
  { id: 'asset-activities', label: 'Activities' },
  { id: 'evidence', label: 'Attach Evidence' },
  { id: 'result', label: 'Result' },
];

const makePreviewSteps = (
  selectStep: FormStep,
  ghostPrefix: string,
  selectLabel: string,
  authLabel: string,
): StepDescriptor[] => [
  { id: 'method', label: 'Data Method' },
  { id: selectStep, label: selectLabel },
  { id: `ghost:${ghostPrefix}-auth`, label: authLabel },
  { id: `ghost:${ghostPrefix}-review`, label: 'Review & Submit' },
  { id: `ghost:${ghostPrefix}-result`, label: 'Result' },
];

export const CONNECT_UTILITY_STEPS = makePreviewSteps('connect-utility-request', 'cu', 'Select Provider', 'Authorize');
export const CONNECT_FINANCIAL_STEPS = makePreviewSteps('connect-financial-request', 'cf', 'Select Provider', 'Authorize');
export const CONNECT_TRAVEL_STEPS = makePreviewSteps('connect-travel-request', 'ct', 'Select Provider', 'Authorize');
/**
 * Scan Invoice (GU-6981) rides on the multi-add entry-step machinery: the
 * dropzone + per-file OCR is implemented as a modal that lives ON the
 * `entry` step (toolbar button + auto-open when the user enters via this
 * method). Steps therefore mirror `LOG_ACTIVITY_STEPS` exactly.
 */
export const SCAN_INVOICE_STEPS: StepDescriptor[] = [
  { id: 'method', label: 'Data Method' },
  { id: 'entry', label: 'Add Activities' },
  { id: 'review', label: 'Review' },
  { id: 'evidence', label: 'Attach Evidence' },
  { id: 'result', label: 'Result' },
];
export const BY_SPEND_STEPS = makePreviewSteps('by-spend-request', 'bs', 'Data Source', 'Spend Details');
export const BULK_SPEND_STEPS = makePreviewSteps('bulk-spend-request', 'bks', 'Data Source', 'Upload File');
export const EMPLOYEE_SURVEY_STEPS = makePreviewSteps('employee-survey-request', 'es', 'Survey Template', 'Recipients');

export const getSteps = (method: DataMethod | null) => {
  switch (method) {
    case DataMethod.BulkUpload:
      return BULK_UPLOAD_STEPS;
    case DataMethod.ByAsset:
      return ASSET_WIZARD_STEPS;
    case DataMethod.ConnectUtility:
      return CONNECT_UTILITY_STEPS;
    case DataMethod.ConnectFinancial:
      return CONNECT_FINANCIAL_STEPS;
    case DataMethod.ConnectTravel:
      return CONNECT_TRAVEL_STEPS;
    case DataMethod.ScanInvoice:
      return SCAN_INVOICE_STEPS;
    case DataMethod.BySpend:
      return BY_SPEND_STEPS;
    case DataMethod.BulkSpend:
      return BULK_SPEND_STEPS;
    case DataMethod.EmployeeSurvey:
      return EMPLOYEE_SURVEY_STEPS;
    case DataMethod.ByActivity:
    case null:
    default:
      return LOG_ACTIVITY_STEPS;
  }
};

export const selectClass =
  'tw:flex tw:h-9 tw:w-full tw:rounded-md tw:border tw:border-input tw:bg-transparent tw:px-3 tw:py-1 tw:text-sm tw:shadow-sm tw:outline-none focus-visible:tw:ring-1 focus-visible:tw:ring-ring tw:cursor-pointer disabled:tw:cursor-not-allowed disabled:tw:opacity-50';
export const inputClass =
  'tw:flex tw:h-9 tw:w-full tw:rounded-md tw:border tw:border-input tw:bg-transparent tw:px-3 tw:py-1 tw:text-sm tw:shadow-sm tw:outline-none focus-visible:tw:ring-1 focus-visible:tw:ring-ring';

export const needsFuelType = (activityType: ActivityType | ''): boolean =>
  activityType === Scope1ActivityType.StationaryCombustion || activityType === Scope1ActivityType.MobileCombustion;

export const needsVehicleType = (activityType: ActivityType | ''): boolean =>
  activityType === Scope1ActivityType.MobileCombustion;

export const needsRefrigerantType = (activityType: ActivityType | ''): boolean =>
  activityType === Scope1ActivityType.FugitiveEmissions;

export const needsProcessType = (activityType: ActivityType | ''): boolean =>
  activityType === Scope1ActivityType.ProcessEmissions;

/**
 * Single source of truth for "is this activity entry filled in enough
 * to save?" — used by every save-button gate (wizard Next, AddActivityModal
 * canSave, AssetActivitiesStep modal). Keeps the rules in one place so
 * Mobile Combustion's required fuelType (and any future per-activity-type
 * required field) can't drift between gates.
 */
export const isActivityFormComplete = (
  values: {
    activityType: ActivityType | '';
    periodStart: string;
    periodEnd: string;
    quantity: string;
    unit: ActivityUnit | '';
    region: string;
    fuelType: FuelType | '';
    vehicleType: VehicleType | '';
    refrigerantType: RefrigerantGasType | FugitiveGasType | '';
    processType: ProcessType | '';
  },
): boolean => {
  if (!values.activityType || !values.periodStart || !values.periodEnd) {
    return false;
  }
  if (!values.quantity || !values.unit || !values.region) {
    return false;
  }
  if (needsFuelType(values.activityType) && !values.fuelType) {
    return false;
  }
  if (needsVehicleType(values.activityType) && !values.vehicleType) {
    return false;
  }
  if (needsRefrigerantType(values.activityType) && !values.refrigerantType) {
    return false;
  }
  if (needsProcessType(values.activityType) && !values.processType) {
    return false;
  }
  return true;
};

export const METHOD_CARDS = [
  {
    key: DataMethod.ByActivity,
    Icon: ClipboardList,
    iconBg: 'tw:bg-emerald-100',
    iconColor: 'tw:text-emerald-700',
    label: 'By Activity',
    description: 'Manually enter a single emission activity',
  },
  {
    key: DataMethod.BulkUpload,
    Icon: FileSpreadsheet,
    iconBg: 'tw:bg-blue-100',
    iconColor: 'tw:text-blue-600',
    label: 'Bulk Activity Import',
    description: 'Import multiple activities from an Excel or CSV file',
  },
  {
    key: DataMethod.ByAsset,
    Icon: Archive,
    iconBg: 'tw:bg-purple-100',
    iconColor: 'tw:text-purple-600',
    label: 'By Asset',
    description: 'Register an asset and log all its associated emission activities',
  },
  {
    key: 'by-spend' as const,
    Icon: DollarSign,
    iconBg: 'tw:bg-rose-100',
    iconColor: 'tw:text-rose-600',
    label: 'By Spend',
    description: 'Calculate Scope 3 emissions from procurement and expense data using spend-based factors',
  },
  {
    key: 'bulk-spend' as const,
    Icon: DollarSign,
    iconBg: 'tw:bg-rose-100',
    iconColor: 'tw:text-rose-600',
    label: 'Bulk Spend Import',
    description: 'Import multiple spend data from an Excel or CSV file',
  },
  {
    key: 'connect-utility' as const,
    Icon: Plug,
    iconBg: 'tw:bg-amber-100',
    iconColor: 'tw:text-amber-600',
    label: 'Connect Utility',
    description: 'Auto-sync electricity, gas, and water bills from your utility provider',
  },
  {
    key: 'connect-financial' as const,
    Icon: Banknote,
    iconBg: 'tw:bg-sky-100',
    iconColor: 'tw:text-sky-600',
    label: 'Connect Financial',
    description: 'Auto-sync spend data from your accounting software',
  },
  {
    key: 'connect-travel' as const,
    Icon: Plane,
    iconBg: 'tw:bg-violet-100',
    iconColor: 'tw:text-violet-600',
    label: 'Connect Travel',
    description: 'Auto-sync business travel and commuting data from your travel platform',
  },
  {
    key: 'scan-invoice' as const,
    Icon: Camera,
    iconBg: 'tw:bg-orange-100',
    iconColor: 'tw:text-orange-600',
    label: 'Scan Invoice',
    description: 'Upload a utility or supplier invoice and auto-extract emission-relevant line items',
  },
  {
    key: 'employee-survey' as const,
    Icon: Users,
    iconBg: 'tw:bg-teal-100',
    iconColor: 'tw:text-teal-600',
    label: 'By Employee Survey',
    description: 'Collect Scope 3 commuting and work-from-home data via employee surveys',
  },
];

/**
 * Step 1 two-level picker model (Claude Design handoff, Variation D):
 * user first picks an Intent, then picks a method archetype within that intent.
 * Archetypes have a stable glyph + palette so "blue folder = bulk upload"
 * reads the same wherever it appears.
 *
 * Each intent can optionally carry provider/integration sections below the
 * archetype grid (utility providers under By Usage, accounting software
 * under By Spend, travel platforms under By Travel). The By Survey intent
 * is structurally different: it has no archetype grid — the intent *is*
 * the archetype — and renders survey templates directly.
 */
export type DataIntent = 'activity' | 'asset' | 'spend' | 'travel' | 'logistics' | 'survey';
export type MethodArchetype = 'manual' | 'bulk' | 'connect' | 'scan' | 'existing';

export type IntentScopeGroup = 'scope1-2' | 'scope3';

export interface IntentDescriptor {
  id: DataIntent;
  title: string;
  caption: string;
  Icon: LucideIcon;
  iconBg: string;
  iconColor: string;
  /** Short example labels rendered as pills under the caption to give users a feel for what fits inside this intent. */
  exampleCategories: string[];
  /** Used by MethodStep to split the grid into Scope 1+2 / Scope 3 rows. */
  scopeGroup: IntentScopeGroup;
}

export const INTENT_CARDS: IntentDescriptor[] = [
  {
    id: 'activity',
    title: 'By Usage',
    caption: 'Log a fuel, energy, or usage activity directly. Covers Scope 1 and 2.',
    Icon: ClipboardList,
    iconBg: 'tw:bg-emerald-100',
    iconColor: 'tw:text-emerald-700',
    exampleCategories: ['Electricity', 'Fuel', 'Refrigerants', 'Industrial processes', 'Emissions', 'Direct', 'Indirect'],
    scopeGroup: 'scope1-2',
  },
  {
    id: 'asset',
    title: 'By Asset',
    caption: 'Register an asset once, then log its activities. Covers Scope 1 and 2.',
    Icon: Archive,
    iconBg: 'tw:bg-sky-100',
    iconColor: 'tw:text-sky-600',
    exampleCategories: ['Office', 'Location', 'Warehouse', 'Vehicle', 'Data centre', 'Equipment', 'Manufacturing facility'],
    scopeGroup: 'scope1-2',
  },
  {
    id: 'spend',
    title: 'By Spend',
    caption: 'Calculate from supplier and expense data. Covers Scope 3.1 (Purchased Goods & Services).',
    Icon: DollarSign,
    iconBg: 'tw:bg-rose-100',
    iconColor: 'tw:text-rose-600',
    exampleCategories: ['Supplier spend', 'Procurement', 'Expenses', 'Goods', 'Services', 'Capital goods'],
    scopeGroup: 'scope3',
  },
  {
    id: 'travel',
    title: 'By Travel',
    caption: 'Log business trips or connect your corporate travel platform. Covers Scope 3.6.',
    Icon: Plane,
    iconBg: 'tw:bg-orange-100',
    iconColor: 'tw:text-orange-600',
    exampleCategories: ['Flights', 'Rail', 'Hotels', 'Rideshare'],
    scopeGroup: 'scope3',
  },
  {
    id: 'logistics',
    title: 'By Shipment',
    caption: 'Log inbound or outbound freight movements by mode, distance and weight. Covers Scope 3.4 and 3.9.',
    Icon: Truck,
    iconBg: 'tw:bg-purple-100',
    iconColor: 'tw:text-purple-700',
    exampleCategories: ['Inbound freight', 'Outbound freight', 'Last-mile'],
    scopeGroup: 'scope3',
  },
  {
    id: 'survey',
    title: 'By Survey',
    caption: 'Collect data from suppliers, employees, lessors, customers, lessees, franchisees and investees via surveys. Covers Scope 3.1, 3.2, 3.7, 3.8, 3.10, 3.13, 3.14 and 3.15.',
    Icon: Users,
    iconBg: 'tw:bg-gray-100',
    iconColor: 'tw:text-gray-600',
    exampleCategories: ['Suppliers', 'Employees', 'Leased assets', 'Customer processing', 'Franchisees', 'Investees'],
    scopeGroup: 'scope3',
  },
];

export interface ArchetypeStyle {
  label: string;
  Icon: LucideIcon;
  /** Square chip background (theme-friendly tw utility). */
  chipBg: string;
  /** Square chip icon colour. */
  chipIcon: string;
  /** Title colour on the tile. */
  titleColor: string;
  /** Hover border colour — mirrors the archetype stroke so hovered tiles glow. */
  hoverBorder: string;
}

export const METHOD_ARCHETYPES: Record<MethodArchetype, ArchetypeStyle> = {
  manual: {
    label: 'Enter manually',
    Icon: PenLine,
    chipBg: 'tw:bg-emerald-100',
    chipIcon: 'tw:text-emerald-700',
    titleColor: 'tw:text-emerald-900',
    hoverBorder: 'tw:hover:border-emerald-500',
  },
  bulk: {
    label: 'File upload',
    Icon: UploadCloud,
    chipBg: 'tw:bg-indigo-100',
    chipIcon: 'tw:text-indigo-700',
    titleColor: 'tw:text-indigo-900',
    hoverBorder: 'tw:hover:border-indigo-500',
  },
  connect: {
    label: 'Automatic',
    Icon: RefreshCw,
    chipBg: 'tw:bg-amber-100',
    chipIcon: 'tw:text-amber-700',
    titleColor: 'tw:text-amber-900',
    hoverBorder: 'tw:hover:border-amber-500',
  },
  scan: {
    label: 'Scan an invoice',
    Icon: ScanLine,
    chipBg: 'tw:bg-orange-100',
    chipIcon: 'tw:text-orange-700',
    titleColor: 'tw:text-orange-900',
    hoverBorder: 'tw:hover:border-orange-500',
  },
  existing: {
    label: 'Manage existing',
    Icon: Pencil,
    chipBg: 'tw:bg-slate-100',
    chipIcon: 'tw:text-slate-700',
    titleColor: 'tw:text-slate-900',
    hoverBorder: 'tw:hover:border-slate-500',
  },
};

/**
 * External routes a method tile can deep-link to instead of advancing the
 * wizard. Kept as a closed string union so the `MethodTile` click handler
 * can resolve each case to a concrete route URL — no arbitrary paths in
 * data.
 */
export type MethodTileExternalRoute = 'manage-assets';

export interface MethodTileDescriptor {
  archetype: MethodArchetype;
  subject: string;
  /** Optional title override — falls back to the archetype's default label. */
  title?: string;
  /** DataMethod that gets activated when this tile is picked. */
  method: DataMethod;
  variant?: ButtonVariant;
  /**
   * When true, clicking the tile fires a Request Access toast instead of
   * navigating to the flow. Used for methods whose backing wizard doesn't
   * yet support the intent (e.g. "one trip" under By Travel — the real
   * activity wizard has no concept of a trip yet).
   */
  requestAccessOnly?: boolean;
  /**
   * When set, clicking the tile navigates to the named external route
   * (e.g. Manage Assets) instead of advancing the wizard. Takes precedence
   * over `requestAccessOnly`.
   */
  externalRoute?: MethodTileExternalRoute;
}

/**
 * Archetype methods available per Intent. The `connect` archetype is no
 * longer surfaced as a method tile — each intent now carries its own
 * provider section (see `INTENT_PROVIDER_SECTIONS` below) so integrations
 * sit alongside their natural home (utilities under By Usage, accounting
 * under By Spend, travel platforms under By Travel). The `survey` intent
 * is deliberately absent here — it renders survey tiles directly and has
 * no archetype grid.
 */
export type IntentWithMethods = Exclude<DataIntent, 'survey'>;

export const INTENT_METHODS: Record<IntentWithMethods, MethodTileDescriptor[]> = {
  activity: [
    // Single entry tile — the new multi-add Entry step has a toolbar with
    // Add / Upload XLSX / Scan Invoice, so the three legacy tiles collapse
    // into one. The `connect-utility` provider section is rendered below.
    { archetype: 'manual', title: 'Add activities', subject: 'Type, upload, or scan — all in one place', method: 'log-activity' },
  ],
  asset: [
    // Bulk + manual paths are unified inside the asset wizard now (XLSX
    // upload lives on `asset-select`), so the method picker exposes a
    // single "By Asset" tile.
    { archetype: 'manual', subject: 'Add assets', method: 'from-inventory', variant: 'default' },
    { archetype: 'existing', subject: 'Add to an asset you already registered', method: 'from-inventory', externalRoute: 'manage-assets' },
  ],
  spend: [
    { archetype: 'manual', subject: 'Spend', method: 'by-spend', variant: 'default' },
    { archetype: 'bulk', subject: 'Upload XLSX', method: 'bulk-spend' },
    { archetype: 'scan', subject: 'Upload Invoice', method: 'scan-invoice' },
  ],
  travel: [
    { archetype: 'manual', subject: 'Trip', method: 'log-activity', requestAccessOnly: true, variant: 'default' },
    { archetype: 'bulk', subject: 'Upload XLSX', method: 'bulk-upload', requestAccessOnly: true },
  ],
  logistics: [
    { archetype: 'manual', subject: 'Shipment', method: 'log-activity', requestAccessOnly: true, variant: 'default' },
    { archetype: 'bulk', subject: 'Upload XLSX', method: 'bulk-upload', requestAccessOnly: true },
    { archetype: 'scan', subject: 'Upload Invoice', method: 'scan-invoice', requestAccessOnly: true },
  ],
};

/**
 * Survey templates for the By Survey intent. Unlike providers, survey tiles
 * show a lucide icon (no brand mark) and currently all fire the same
 * Request Access toast with the template label.
 */
export interface SurveyTile {
  id: string;
  label: string;
  subject: string;
  Icon: LucideIcon;
  iconBg: string;
  iconColor: string;
  titleColor: string;
}

export const VEHICLE_TYPE_CARDS: TypeCardOption[] = [
  { value: VehicleType.Car, ...VEHICLE_TYPE_LABEL_ENTRIES[VehicleType.Car], Icon: Car, iconBg: 'tw:bg-blue-100', iconColor: 'tw:text-blue-600' },
  { value: VehicleType.SmallCar, ...VEHICLE_TYPE_LABEL_ENTRIES[VehicleType.SmallCar], Icon: Car, iconBg: 'tw:bg-blue-100', iconColor: 'tw:text-blue-600' },
  { value: VehicleType.MediumCar, ...VEHICLE_TYPE_LABEL_ENTRIES[VehicleType.MediumCar], Icon: Car, iconBg: 'tw:bg-blue-100', iconColor: 'tw:text-blue-600' },
  { value: VehicleType.LargeCar, ...VEHICLE_TYPE_LABEL_ENTRIES[VehicleType.LargeCar], Icon: Car, iconBg: 'tw:bg-blue-100', iconColor: 'tw:text-blue-600' },
  { value: VehicleType.Van, ...VEHICLE_TYPE_LABEL_ENTRIES[VehicleType.Van], Icon: Truck, iconBg: 'tw:bg-slate-100', iconColor: 'tw:text-slate-600' },
  { value: VehicleType.HGV, ...VEHICLE_TYPE_LABEL_ENTRIES[VehicleType.HGV], Icon: Truck, iconBg: 'tw:bg-slate-100', iconColor: 'tw:text-slate-600' },
  { value: VehicleType.Motorcycle, ...VEHICLE_TYPE_LABEL_ENTRIES[VehicleType.Motorcycle], Icon: Bike, iconBg: 'tw:bg-orange-100', iconColor: 'tw:text-orange-600' },
];

export const FUEL_TYPE_CARDS: TypeCardOption[] = [
  // Gaseous
  { value: FuelType.NaturalGas, ...FUEL_TYPE_LABEL_ENTRIES[FuelType.NaturalGas], Icon: Flame, iconBg: 'tw:bg-orange-100', iconColor: 'tw:text-orange-600' },
  { value: FuelType.LPG, ...FUEL_TYPE_LABEL_ENTRIES[FuelType.LPG], Icon: Flame, iconBg: 'tw:bg-orange-100', iconColor: 'tw:text-orange-500' },
  { value: FuelType.Propane, ...FUEL_TYPE_LABEL_ENTRIES[FuelType.Propane], Icon: Flame, iconBg: 'tw:bg-amber-100', iconColor: 'tw:text-amber-600' },
  { value: FuelType.Butane, ...FUEL_TYPE_LABEL_ENTRIES[FuelType.Butane], Icon: Flame, iconBg: 'tw:bg-amber-100', iconColor: 'tw:text-amber-500' },
  { value: FuelType.CNG, ...FUEL_TYPE_LABEL_ENTRIES[FuelType.CNG], Icon: Fuel, iconBg: 'tw:bg-sky-100', iconColor: 'tw:text-sky-600' },
  { value: FuelType.LNG, ...FUEL_TYPE_LABEL_ENTRIES[FuelType.LNG], Icon: Fuel, iconBg: 'tw:bg-sky-100', iconColor: 'tw:text-sky-500' },
  { value: FuelType.Hydrogen, ...FUEL_TYPE_LABEL_ENTRIES[FuelType.Hydrogen], Icon: Zap, iconBg: 'tw:bg-yellow-100', iconColor: 'tw:text-yellow-600' },
  // Liquid
  { value: FuelType.Diesel, ...FUEL_TYPE_LABEL_ENTRIES[FuelType.Diesel], Icon: Droplets, iconBg: 'tw:bg-slate-100', iconColor: 'tw:text-slate-600' },
  { value: FuelType.Petrol, ...FUEL_TYPE_LABEL_ENTRIES[FuelType.Petrol], Icon: Droplets, iconBg: 'tw:bg-red-100', iconColor: 'tw:text-red-500' },
  { value: FuelType.Biodiesel, ...FUEL_TYPE_LABEL_ENTRIES[FuelType.Biodiesel], Icon: Leaf, iconBg: 'tw:bg-green-100', iconColor: 'tw:text-green-600' },
  { value: FuelType.HeavyFuelOil, ...FUEL_TYPE_LABEL_ENTRIES[FuelType.HeavyFuelOil], Icon: Droplets, iconBg: 'tw:bg-stone-100', iconColor: 'tw:text-stone-600' },
  { value: FuelType.LightFuelOil, ...FUEL_TYPE_LABEL_ENTRIES[FuelType.LightFuelOil], Icon: Droplets, iconBg: 'tw:bg-stone-100', iconColor: 'tw:text-stone-500' },
  { value: FuelType.Kerosene, ...FUEL_TYPE_LABEL_ENTRIES[FuelType.Kerosene], Icon: Droplets, iconBg: 'tw:bg-yellow-100', iconColor: 'tw:text-yellow-500' },
  { value: FuelType.JetFuel, ...FUEL_TYPE_LABEL_ENTRIES[FuelType.JetFuel], Icon: Plane, iconBg: 'tw:bg-indigo-100', iconColor: 'tw:text-indigo-600' },
  { value: FuelType.Ethanol, ...FUEL_TYPE_LABEL_ENTRIES[FuelType.Ethanol], Icon: FlaskConical, iconBg: 'tw:bg-lime-100', iconColor: 'tw:text-lime-600' },
  { value: FuelType.HVO, ...FUEL_TYPE_LABEL_ENTRIES[FuelType.HVO], Icon: Leaf, iconBg: 'tw:bg-green-100', iconColor: 'tw:text-green-500' },
  // Coal
  { value: FuelType.Coal, ...FUEL_TYPE_LABEL_ENTRIES[FuelType.Coal], Icon: Layers, iconBg: 'tw:bg-neutral-200', iconColor: 'tw:text-neutral-600' },
  { value: FuelType.CoalIndustrial, ...FUEL_TYPE_LABEL_ENTRIES[FuelType.CoalIndustrial], Icon: Layers, iconBg: 'tw:bg-neutral-200', iconColor: 'tw:text-neutral-600' },
  { value: FuelType.CoalDomestic, ...FUEL_TYPE_LABEL_ENTRIES[FuelType.CoalDomestic], Icon: Layers, iconBg: 'tw:bg-neutral-200', iconColor: 'tw:text-neutral-500' },
  { value: FuelType.Anthracite, ...FUEL_TYPE_LABEL_ENTRIES[FuelType.Anthracite], Icon: Layers, iconBg: 'tw:bg-zinc-200', iconColor: 'tw:text-zinc-700' },
  { value: FuelType.Coke, ...FUEL_TYPE_LABEL_ENTRIES[FuelType.Coke], Icon: Layers, iconBg: 'tw:bg-zinc-200', iconColor: 'tw:text-zinc-600' },
  // Wood / Biomass
  { value: FuelType.Wood, ...FUEL_TYPE_LABEL_ENTRIES[FuelType.Wood], Icon: Leaf, iconBg: 'tw:bg-green-100', iconColor: 'tw:text-green-700' },
  { value: FuelType.WoodLogs, ...FUEL_TYPE_LABEL_ENTRIES[FuelType.WoodLogs], Icon: Leaf, iconBg: 'tw:bg-green-100', iconColor: 'tw:text-green-700' },
  { value: FuelType.WoodChips, ...FUEL_TYPE_LABEL_ENTRIES[FuelType.WoodChips], Icon: Leaf, iconBg: 'tw:bg-green-100', iconColor: 'tw:text-green-600' },
  { value: FuelType.WoodPellets, ...FUEL_TYPE_LABEL_ENTRIES[FuelType.WoodPellets], Icon: Leaf, iconBg: 'tw:bg-green-100', iconColor: 'tw:text-green-500' },
  { value: FuelType.Biomass, ...FUEL_TYPE_LABEL_ENTRIES[FuelType.Biomass], Icon: Leaf, iconBg: 'tw:bg-teal-100', iconColor: 'tw:text-teal-600' },
  // Electric / Alternative
  { value: FuelType.Electric, ...FUEL_TYPE_LABEL_ENTRIES[FuelType.Electric], Icon: Zap, iconBg: 'tw:bg-yellow-100', iconColor: 'tw:text-yellow-500' },
  { value: FuelType.PlugInHybrid, ...FUEL_TYPE_LABEL_ENTRIES[FuelType.PlugInHybrid], Icon: Zap, iconBg: 'tw:bg-lime-100', iconColor: 'tw:text-lime-600' },
  { value: FuelType.Hybrid, ...FUEL_TYPE_LABEL_ENTRIES[FuelType.Hybrid], Icon: Zap, iconBg: 'tw:bg-lime-100', iconColor: 'tw:text-lime-500' },
  { value: FuelType.Unknown, ...FUEL_TYPE_LABEL_ENTRIES[FuelType.Unknown], Icon: Fuel, iconBg: 'tw:bg-gray-100', iconColor: 'tw:text-gray-400' },
];

export const REFRIGERANT_TYPE_CARDS: TypeCardOption[] = [
  { value: RefrigerantGasType.R134A, ...GAS_TYPE_LABEL_ENTRIES[RefrigerantGasType.R134A], Icon: Snowflake, iconBg: 'tw:bg-sky-100', iconColor: 'tw:text-sky-600' },
  { value: RefrigerantGasType.R410A, ...GAS_TYPE_LABEL_ENTRIES[RefrigerantGasType.R410A], Icon: Snowflake, iconBg: 'tw:bg-sky-100', iconColor: 'tw:text-sky-600' },
  { value: RefrigerantGasType.R404A, ...GAS_TYPE_LABEL_ENTRIES[RefrigerantGasType.R404A], Icon: Snowflake, iconBg: 'tw:bg-sky-100', iconColor: 'tw:text-sky-600' },
  { value: RefrigerantGasType.R407C, ...GAS_TYPE_LABEL_ENTRIES[RefrigerantGasType.R407C], Icon: Snowflake, iconBg: 'tw:bg-sky-100', iconColor: 'tw:text-sky-600' },
  { value: RefrigerantGasType.R507A, ...GAS_TYPE_LABEL_ENTRIES[RefrigerantGasType.R507A], Icon: Snowflake, iconBg: 'tw:bg-sky-100', iconColor: 'tw:text-sky-600' },
  { value: RefrigerantGasType.R422D, ...GAS_TYPE_LABEL_ENTRIES[RefrigerantGasType.R422D], Icon: Snowflake, iconBg: 'tw:bg-sky-100', iconColor: 'tw:text-sky-600' },
  { value: RefrigerantGasType.R32, ...GAS_TYPE_LABEL_ENTRIES[RefrigerantGasType.R32], Icon: Snowflake, iconBg: 'tw:bg-cyan-100', iconColor: 'tw:text-cyan-600' },
  { value: RefrigerantGasType.R1234yf, ...GAS_TYPE_LABEL_ENTRIES[RefrigerantGasType.R1234yf], Icon: Snowflake, iconBg: 'tw:bg-cyan-100', iconColor: 'tw:text-cyan-600' },
  { value: RefrigerantGasType.R290, ...GAS_TYPE_LABEL_ENTRIES[RefrigerantGasType.R290], Icon: Snowflake, iconBg: 'tw:bg-teal-100', iconColor: 'tw:text-teal-600' },
  { value: RefrigerantGasType.R600A, ...GAS_TYPE_LABEL_ENTRIES[RefrigerantGasType.R600A], Icon: Snowflake, iconBg: 'tw:bg-teal-100', iconColor: 'tw:text-teal-600' },
  { value: RefrigerantGasType.R744, ...GAS_TYPE_LABEL_ENTRIES[RefrigerantGasType.R744], Icon: CloudFog, iconBg: 'tw:bg-slate-100', iconColor: 'tw:text-slate-500' },
  { value: RefrigerantGasType.R717, ...GAS_TYPE_LABEL_ENTRIES[RefrigerantGasType.R717], Icon: CloudFog, iconBg: 'tw:bg-yellow-100', iconColor: 'tw:text-yellow-600' },
  { value: RefrigerantGasType.R22, ...GAS_TYPE_LABEL_ENTRIES[RefrigerantGasType.R22], Icon: Snowflake, iconBg: 'tw:bg-blue-100', iconColor: 'tw:text-blue-500' },
  { value: RefrigerantGasType.R12, ...GAS_TYPE_LABEL_ENTRIES[RefrigerantGasType.R12], Icon: Snowflake, iconBg: 'tw:bg-blue-100', iconColor: 'tw:text-blue-400' },
  { value: FugitiveGasType.SF6, ...GAS_TYPE_LABEL_ENTRIES[FugitiveGasType.SF6], Icon: Atom, iconBg: 'tw:bg-purple-100', iconColor: 'tw:text-purple-600' },
  { value: FugitiveGasType.NF3, ...GAS_TYPE_LABEL_ENTRIES[FugitiveGasType.NF3], Icon: Atom, iconBg: 'tw:bg-purple-100', iconColor: 'tw:text-purple-600' },
  { value: FugitiveGasType.CF4, ...GAS_TYPE_LABEL_ENTRIES[FugitiveGasType.CF4], Icon: Atom, iconBg: 'tw:bg-violet-100', iconColor: 'tw:text-violet-600' },
  { value: FugitiveGasType.C2F6, ...GAS_TYPE_LABEL_ENTRIES[FugitiveGasType.C2F6], Icon: Atom, iconBg: 'tw:bg-violet-100', iconColor: 'tw:text-violet-600' },
  { value: FugitiveGasType.CHF3, ...GAS_TYPE_LABEL_ENTRIES[FugitiveGasType.CHF3], Icon: Atom, iconBg: 'tw:bg-violet-100', iconColor: 'tw:text-violet-600' },
];

export const PROCESS_TYPE_CARDS: TypeCardOption[] = [
  { value: ProcessType.CementProduction, ...PROCESS_TYPE_LABEL_ENTRIES[ProcessType.CementProduction], Icon: Building2, iconBg: 'tw:bg-stone-100', iconColor: 'tw:text-stone-600' },
  { value: ProcessType.SteelProduction, ...PROCESS_TYPE_LABEL_ENTRIES[ProcessType.SteelProduction], Icon: Hammer, iconBg: 'tw:bg-slate-100', iconColor: 'tw:text-slate-600' },
  // ChemicalSynthesis disabled — too broad, @TODO freetext search UX
  { value: ProcessType.GlassManufacturing, ...PROCESS_TYPE_LABEL_ENTRIES[ProcessType.GlassManufacturing], Icon: Layers, iconBg: 'tw:bg-cyan-100', iconColor: 'tw:text-cyan-600' },
  { value: ProcessType.LimeProduction, ...PROCESS_TYPE_LABEL_ENTRIES[ProcessType.LimeProduction], Icon: Mountain, iconBg: 'tw:bg-amber-100', iconColor: 'tw:text-amber-600' },
];
